import {
  FilterValueType,
  getFilterDefinitionByKey,
  type FilterDefinition,
  type StructuredSearchSchema,
} from "./structured-search-schema";

export enum VersionedEntityKind {
  Prompt = "prompt",
  Workflow = "workflow",
}

/** Maps a trace filter field to its version-ref backend field and version suggestion source. */
export interface VersionedEntityConfig {
  kind: VersionedEntityKind;
  /** Backend field emitted when a specific version is selected (e.g. trace_prompt_version_refs). */
  versionRefField: string;
  /** Suggestion field used to load version numbers for the selected entity. */
  versionsSuggestionField: string;
}

export const getVersionedEntityConfig = (
  schema: StructuredSearchSchema,
  fieldKey: string | undefined,
): VersionedEntityConfig | undefined => {
  const definition = getFilterDefinitionByKey(schema, fieldKey);
  if (!definition) {
    return undefined;
  }

  if (definition.type === FilterValueType.Prompt) {
    return {
      kind: VersionedEntityKind.Prompt,
      versionRefField: definition.key,
      versionsSuggestionField: "prompt",
    };
  }

  return definition.versionedEntity;
};

export const isVersionedEntityField = (
  schema: StructuredSearchSchema,
  fieldKey: string | undefined,
): boolean => getVersionedEntityConfig(schema, fieldKey) !== undefined;

export const isPromptVersionedEntityField = (
  schema: StructuredSearchSchema,
  fieldKey: string | undefined,
): boolean => getVersionedEntityConfig(schema, fieldKey)?.kind === VersionedEntityKind.Prompt;

export const isWorkflowVersionedEntityField = (
  schema: StructuredSearchSchema,
  fieldKey: string | undefined,
): boolean => getVersionedEntityConfig(schema, fieldKey)?.kind === VersionedEntityKind.Workflow;

export const filterHasVersionedEntityConfig = (
  definition: FilterDefinition,
): definition is FilterDefinition & { versionedEntity: VersionedEntityConfig } =>
  definition.versionedEntity !== undefined;
